import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Button from "../../components/inputs/button";
import Loader from "../../components/misc/loader";
import infoSvg from "../../assets/svgs/info.svg";
import CriteriaGroupsDisplay from "./CriteriaGroupsDisplay";
import FeedbackModal, { FEEDBACK_REASONS } from "./FeedbackModal";

function formatNumber(num) {
	const formatter = Intl.NumberFormat("en-Us", { notation: "compact" });
	return formatter.format(num);
}

const themeBannerIcon = (
	<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><g clipPath="url(#clip0_2938_17564)"><path d="M12.8334 6.99984C12.8334 8.15356 12.4913 9.28138 11.8503 10.2407C11.2093 11.1999 10.2983 11.9476 9.2324 12.3891C8.1665 12.8306 6.99361 12.9462 5.86206 12.7211C4.7305 12.496 3.6911 11.9404 2.87529 11.1246C2.05949 10.3088 1.50392 9.26942 1.27884 8.13786C1.05376 7.00631 1.16928 5.83342 1.61079 4.76752C2.0523 3.70161 2.79997 2.79057 3.75926 2.1496C4.71854 1.50862 5.84636 1.1665 7.00008 1.1665" stroke="#494BD4" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/><path d="M12.8333 1.1665L7 6.99984" stroke="#494BD4" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/><path d="M9.33325 1.1665H12.8333V4.6665" stroke="#494BD4" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/></g><defs><clipPath id="clip0_2938_17564"><rect width="14" height="14" fill="white"/></clipPath></defs></svg>
);

const ThumbsUpIcon = ({ active }) => (
	<svg width="20" height="20" viewBox="0 0 24 24" fill={active ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<path d="M7 10v12" /><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z" />
	</svg>
);

ThumbsUpIcon.propTypes = { active: PropTypes.bool };

const ThumbsDownIcon = ({ active }) => (
	<svg width="20" height="20" viewBox="0 0 24 24" fill={active ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<path d="M17 14V2" /><path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22h0a3.13 3.13 0 0 1-3-3.88Z" />
	</svg>
);

ThumbsDownIcon.propTypes = { active: PropTypes.bool };

function AudienceCard({ audience, cardIndex, sizingData, sizingIsLoading, sizingFailed = false, isCollapsed, onToggleCollapse, onCreateAudience, count3PSegments, showThemeBanner = true, generationId = null, onSubmitFeedback = null, readOnly = false, existingFeedback = null, existingFeedbackList = null, onFetchSizing = null, criteriaConfiguration, customUserDataConfiguration, modifierConfiguration }) {
	const [rating, setRating] = useState(existingFeedback?.rating || null);
	const [showFeedbackModal, setShowFeedbackModal] = useState(false);
	const [feedbackSent, setFeedbackSent] = useState(!!existingFeedback);

	useEffect(() => {
		if (!readOnly) {
			setRating(null);
			setShowFeedbackModal(false);
			setFeedbackSent(false);
		}
	}, [generationId, readOnly]);

	const handleLike = () => {
		if (readOnly) return;
		const newRating = rating === "like" ? null : "like";
		setRating(newRating);
		if (newRating && onSubmitFeedback) {
			onSubmitFeedback(cardIndex, "like");
		}
	};

	const handleDislike = () => {
		if (readOnly) return;
		if (rating === "dislike") {
			setRating(null);
			return;
		}
		setRating("dislike");
		setShowFeedbackModal(true);
		if (onSubmitFeedback) {
			onSubmitFeedback(cardIndex, "dislike");
		}
	};

	const handleFeedbackSubmit = (reasons, details) => {
		if (onSubmitFeedback) {
			onSubmitFeedback(cardIndex, "dislike", reasons, details);
		}
		setShowFeedbackModal(false);
		setFeedbackSent(true);
	};

	return (
		<div className="audience-card">
			<div className="card-header">
				<div className="header-left">
					<h4>{audience.name}</h4>
				</div>
				{!readOnly && (
					<div className="header-right">
						<Button text="Create Audience" type="button" style="light"
							disabled={false} loaded={false}
							onClick={() => onCreateAudience(audience)} />
					</div>
				)}
			</div>

			{showThemeBanner && (audience.theme || audience.differentiation) && (
				<div className="theme-banner">
					{themeBannerIcon}
					{audience.theme && <span className="theme-label">{audience.theme}</span>}
					{audience.differentiation && <span className="theme-detail">{audience.differentiation}</span>}
				</div>
			)}

			<div className={`audience-content-row ${isCollapsed ? "minimized" : ""}`}>
				{audience.composite_conditions && (
					<div className="audience-criteria">
						<CriteriaGroupsDisplay
							criteriaGroupCondition={audience.composite_conditions.criteriaGroupCondition}
							criteriaGroups={audience.composite_conditions.criteriaGroups}
							criteriaConfiguration={criteriaConfiguration}
							customUserDataConfiguration={customUserDataConfiguration}
							modifierConfiguration={modifierConfiguration}
						/>
					</div>
				)}

				<div className="stats-panel">
					<div className="stat">
						<label>3P Segments</label>
						<value>{count3PSegments(audience)}</value>
					</div>
					<div className="stat">
						<label>UUs</label>
						<value>
							{sizingIsLoading ? (
								<Loader size="xs" />
							) : sizingFailed ? (
								"—"
							) : sizingData?.uniqueUsers ? (
								formatNumber(sizingData.uniqueUsers)
							) : readOnly && onFetchSizing && !sizingData ? (
								<button type="button" className="fetch-sizing-btn" onClick={() => onFetchSizing(audience, cardIndex)}>Fetch</button>
							) : (
								0
							)}
						</value>
					</div>
					<div className="stat">
						<label>PVs</label>
						<value>
							{sizingIsLoading ? (
								<Loader size="xs" />
							) : sizingFailed ? (
								"—"
							) : sizingData?.pageViews ? (
								formatNumber(sizingData.pageViews)
							) : readOnly && onFetchSizing && !sizingData ? (
								<button type="button" className="fetch-sizing-btn" onClick={() => onFetchSizing(audience, cardIndex)}>Fetch</button>
							) : (
								0
							)}
						</value>
					</div>
					<span className="lookback-label"
						data-tooltip-id="lookback-tooltip"
						data-tooltip-content="30-day estimate based on historical data"
					>
						30-day lookback
						<img src={infoSvg} alt="info" className="lookback-info-icon" />
					</span>
				</div>
			</div>
			<div className="card-footer">
				{readOnly && (existingFeedbackList?.length || existingFeedback) ? (
					<div className="feedback-buttons read-only">
						{(existingFeedbackList?.length ? existingFeedbackList : [existingFeedback]).filter(Boolean).map((fb, i) => (
							<div className="feedback-entry" key={fb.id ?? i}>
								<span className={`feedback-btn active ${fb.rating}`} title={`Rated: ${fb.rating}`}>
									{fb.rating === "like" ? <ThumbsUpIcon active /> : <ThumbsDownIcon active />}
								</span>
								{fb.user_name && (
									<span className="feedback-user">by {fb.user_name}</span>
								)}
								{fb.reasons?.length > 0 && (
									<span className="feedback-reasons">{fb.reasons.map((r) => {
										const match = FEEDBACK_REASONS.find((fr) => fr.value === r);
										return match ? match.label : r;
									}).join(", ")}</span>
								)}
								{fb.details && (
									<span className="feedback-details-text">{fb.details}</span>
								)}
							</div>
						))}
					</div>
				) : generationId && !readOnly ? (
					<div className="feedback-buttons">
						<button
							type="button"
							className={`feedback-btn${rating === "like" ? " active like" : ""}`}
							onClick={handleLike}
							title="Like this audience"
						>
							<ThumbsUpIcon active={rating === "like"} />
						</button>
						<button
							type="button"
							className={`feedback-btn${rating === "dislike" ? " active dislike" : ""}`}
							onClick={handleDislike}
							title="Dislike this audience"
						>
							<ThumbsDownIcon active={rating === "dislike"} />
						</button>
						{rating === "dislike" && (
							<button
								type="button"
								className={`feedback-details-link${feedbackSent ? " sent" : ""}`}
								onClick={() => !feedbackSent && setShowFeedbackModal(true)}
								disabled={feedbackSent}
							>
								{feedbackSent ? "Feedback Sent" : "Add Feedback"}
							</button>
						)}
					</div>
				) : null}
				<button
					type="button"
					className="collapse-toggle"
					onClick={() => onToggleCollapse(cardIndex)}
				>
					{isCollapsed ? "View Details" : "Collapse"}
					<svg className={`collapse-chevron ${isCollapsed ? "collapsed" : ""}`} width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.5 7.5L6 4L9.5 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
				</button>
			</div>

			{showFeedbackModal && (
				<FeedbackModal
					audienceName={audience.name}
					onSubmit={handleFeedbackSubmit}
					onClose={() => setShowFeedbackModal(false)}
				/>
			)}
		</div>
	);
}

AudienceCard.propTypes = {
	audience: PropTypes.object.isRequired,
	cardIndex: PropTypes.number.isRequired,
	sizingData: PropTypes.object,
	sizingIsLoading: PropTypes.bool,
	sizingFailed: PropTypes.bool,
	isCollapsed: PropTypes.bool.isRequired,
	onToggleCollapse: PropTypes.func.isRequired,
	onCreateAudience: PropTypes.func,
	count3PSegments: PropTypes.func.isRequired,
	showThemeBanner: PropTypes.bool,
	generationId: PropTypes.string,
	onSubmitFeedback: PropTypes.func,
	readOnly: PropTypes.bool,
	existingFeedback: PropTypes.object,
	existingFeedbackList: PropTypes.array,
	onFetchSizing: PropTypes.func,
	criteriaConfiguration: PropTypes.array,
	customUserDataConfiguration: PropTypes.array,
	modifierConfiguration: PropTypes.array,
};

export default AudienceCard;
